import clsx from 'clsx';
import React, { FC, useCallback, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KTIcon } from '../../../helpers';
import { get_all_unread_notifications, get_read_notification } from '../drawer-messenger/api/notificationApi';
import { useQuery, useMutation, useQueryClient } from 'react-query';

interface NotificationItem {
  id: number | string;
  data: { [key: string]: any };
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

const unreadNotificationsQueryKey = ['get_all_unread_notifications'];

export const HeaderNotificationsMenu: FC<{ setUnreadCount: (count: number) => void }> = ({ setUnreadCount }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleFakeOutsideClick = () => {
    document.body.click();
  };

  const queryConfig = useMemo(() => ({
  refetchInterval: 6000,
  select: (data: any) => {
    if (Array.isArray(data?.data)) {
      return {
        originalData: data.data,
        data: data.data.slice(0, 5),
      };
    }
    return data;
  },
}), []);

  const unreadNotificationsQuery = useQuery(unreadNotificationsQueryKey, get_all_unread_notifications, queryConfig);

  const readNotificationMutation = useMutation(
    (notificationId: number | string) => get_read_notification(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(unreadNotificationsQueryKey);
      },
    }
  );

  const displayedNotifications = useMemo(() =>
    unreadNotificationsQuery.data?.data || [],
    [unreadNotificationsQuery.data?.data]
  );

  const totalUnreadCount = useMemo(() =>
  unreadNotificationsQuery.data?.data?.length || 0,
  [unreadNotificationsQuery.data?.data?.length]
);

  useEffect(() => {
    setUnreadCount(totalUnreadCount);
  }, [totalUnreadCount, setUnreadCount]);

  const handleNotificationClick = useCallback((notification: NotificationItem) => {
    if (!notification.read_at) {
      readNotificationMutation.mutate(notification.id);
    }
  }, [readNotificationMutation]);

  return (
    <div className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-400px' data-kt-menu='true'>
      <div className='d-flex flex-column bgi-no-repeat rounded-top px-9 pt-10 pb-2' style={{ backgroundColor: '#1E1E2D' }}>
        <h3 className='text-white fw-semibold mb-3'>
          Notifications
          {totalUnreadCount > 0 && (
            <span className='badge bg-primary badge-circle fw-bold fs-8 ms-2'>{totalUnreadCount}</span>
          )}
        </h3>
      </div>

      <div className="tab-content">
        <div className='tab-pane fade show active'>
          <div className='scroll-y mh-350px my-2 px-2'>
            {displayedNotifications.length === 0 ? (
              <div className="text-center text-muted py-10 px-5">
                <KTIcon iconName='check-circle' className='fs-4tx text-success mb-5' />
                <div className='fw-semibold fs-4'>All caught up!</div>
                <div className='text-gray-500 fs-7'>You have no unread notifications.</div>
              </div>
            ) : (
              <ul className="list-group list-group-flush">
                {displayedNotifications.map(notification => (
                  <li
                    key={notification.id}
                    className='list-group-item list-group-item-action d-flex justify-content-between align-items-start py-3 px-4'
                    onClick={() => handleNotificationClick(notification)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      <h5 className='mb-1 fw-semibold'>New Notification</h5>
                      <p className='mb-0 small text-muted'>Received: {new Date(notification.created_at).toLocaleString()}</p>
                    </div>
                    {!notification.read_at && (
                      <span className='badge bg-success'>New</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className='py-3 text-center border-top'>
            <Link
              to='/notifications'
              className='btn btn-color-gray-600 btn-active-color-primary'
              onClick={handleFakeOutsideClick}
            >
              View All Notifications <KTIcon iconName='arrow-right' className='fs-5' />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
